import { ContentfulRichText, EntryRef } from "frontend/contentful/schema/sys";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { useContentful } from "frontend/hooks/use-contentful";
import { usePageNavigation } from "frontend/hooks/use-page-navigation";
import { ContentfulHead } from "../primitives/head.contentful";
import { createSiteHeaderProps } from "../universal/site-header.props";
import { createSiteFooterProps } from "../universal/site-footer.props";
import { TermEntry } from "frontend/contentful/schema/semantics";
import { DatabasePageEntry } from "frontend/contentful/schema/pages";
import {
  DetailPage,
  DetailPageTopper,
} from "design-system/components/pages/detail-page/detail-page";
import { SimplePageTopperProps } from "design-system/components/toppers/simple-page-topper/simple-page-topper";
import { RichText } from "design-system/components/blocks/rich-text/rich-text";
import { Truncator } from "design-system/components/blocks/truncator/truncator";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";
import { Accordion } from "design-system/components/blocks/accordion/accordion";
import { AccordionItemProps } from "design-system/components/blocks/accordion/accordion-item";
import { CtaLinkProps } from "design-system/components/primitives/cta-link/cta-link";
import { ContentfulPageSections } from "../pages/page-sections.contentful";
import { LazyTeaseRowTease } from "design-system/components/blocks/tease-row/tease-row-tease";
import { TeaseRow } from "design-system/components/blocks/tease-row/tease-row";
import { PageSection } from "design-system/components/layouts/page-section/page-section";

export const ContentfulDatabasePage =
  createContentfulComponent<DatabasePageEntry>(
    ({ entry, createEditAttributes }) => {
      const { site, getEntry, getEntryUrl, getEntries } = useContentful();
      const { breadcrumbs } = usePageNavigation();

      const topper: SimplePageTopperProps = {
        title: entry.fields.title,
        theme: "crimson",
        editAttributes: {
          title: createEditAttributes({ entry, fieldId: "title" }),
        },
      };

      const databaseTopper: DetailPageTopper = {
        name: "SimplePageTopper",
        props: topper,
      };

      const header = createSiteHeaderProps(site);
      if (header && topper.theme === "crimson") header.theme = "black";

      const buildAccordionItem = (
        title: string,
        accessMethod?: Array<EntryRef<TermEntry>>,
        accessDescription?: ContentfulRichText,
        accessLocation?: EntryRef<TermEntry>,
      ) => {
        const accessLocationTerm = getEntry(accessLocation);
        let subtitle = "";
        let primaryCta = undefined;
        let secondaryCta = undefined;

        if (accessLocationTerm?.fields.title === "No Access") {
          return {
            title,
            subtitle: "No access at this time",
            disabled: true,
          } as AccordionItemProps;
        }

        if (accessLocationTerm?.fields.title === "Baker Library Only") {
          subtitle = "In person access only";

          const libraryUrl = getEntryUrl(entry.fields.libraryHoursUrl);

          if (libraryUrl) {
            secondaryCta = {
              children: "View Library Hours",
              href: libraryUrl,
            };
          }
        } else {
          subtitle = "Remote access available";
          const accessMethodCtas: Array<CtaLinkProps> = [];
          accessMethod &&
            accessMethod.forEach((method) => {
              const title = getEntry(method)?.fields.title;
              switch (title) {
                case "HarvardKey":
                  accessMethodCtas.push({
                    children: "Access with HarvardKey",
                    href: getEntryUrl(entry.fields.harvardkeyUrl),
                  });
                  break;
                case "HBS Intranet":
                  accessMethodCtas.push({
                    children: "Access with HBS Intranet",
                    href: getEntryUrl(entry.fields.hbsIntranetUrl),
                  });
                  break;
                case "Log into Account":
                  accessMethodCtas.push({
                    children: "Log Into Account",
                    href: getEntryUrl(entry.fields.individualAccountLoginUrl),
                  });
                  break;
                case "Set Up Account":
                  accessMethodCtas.push({
                    children: "Set Up Account",
                    href: getEntryUrl(
                      entry.fields.individualAccountRegisterUrl,
                    ),
                  });
                  break;
                case "No Access":
                default:
                  break;
              }
            });

          if (accessMethodCtas.length) {
            primaryCta = accessMethodCtas[0];
            if (accessMethodCtas.length > 1) {
              secondaryCta = accessMethodCtas[1];
            }
          }
        }

        return {
          title,
          subtitle,
          children: <RenderContentfulRichText document={accessDescription} />,
          primaryCta,
          secondaryCta,
        } as AccordionItemProps;
      };

      const getRelatedObjects = () => {
        if (!entry.fields.relatedObjects) {
          return;
        }
        const relatedObjects = getEntries(entry.fields.relatedObjects);
        if (!relatedObjects.length) {
          return;
        }

        return relatedObjects.map((document) => {
          const url = getEntryUrl(document);

          return {
            name: "ArticleTease",
            props: {
              type: "Article",
              headingLevel: "h5",
              title: document.fields?.title ? document.fields.title : "",
              link: url ? url : "",
              style: "content",
              isSmall: true,
              theme: "white",
            },
          } as LazyTeaseRowTease;
        });
      };

      return (
        <>
          <ContentfulHead entry={entry.fields.seo} />
          <DetailPage
            breadcrumbs={breadcrumbs}
            header={{ name: "SiteHeader", props: header }}
            footer={createSiteFooterProps(site?.fields.footer)}
            topper={databaseTopper}
            theme="white"
            body={{
              children: (
                <>
                  {/* Body Description */}
                  <RichText
                    editAttribute={createEditAttributes({
                      entry,
                      fieldId: "shortDescription",
                    })}
                  >
                    <RenderContentfulRichText
                      document={entry.fields?.shortDescription}
                    />
                  </RichText>
                  {entry.fields.description && (
                    <Truncator
                      align="center"
                      labelHidden="Read More"
                      labelVisible="Hide"
                    >
                      <RichText
                        editAttribute={createEditAttributes({
                          entry,
                          fieldId: "description",
                        })}
                      >
                        <RenderContentfulRichText
                          document={entry.fields.description}
                        />
                      </RichText>
                    </Truncator>
                  )}

                  {/* Access Methods */}
                  <Accordion
                    align="center"
                    headerContent={{ isSmall: false, title: "Get Access" }}
                    items={[
                      buildAccordionItem(
                        "HBS Faculty, Students, Staff & Executive Fellows",
                        entry.fields.accessMethodHbsFacultyStudentsStaff,
                        entry.fields.accessDescriptionHbsFacultyStudentsStaff,
                        entry.fields.accessLocationHbsFacultyStudentsStaff,
                      ),
                      buildAccordionItem(
                        "HBS Executive Education & Post-MBA Fellows",
                        entry.fields.accessMethodHbsExecEdFellows,
                        entry.fields.accessDescriptionHbsExecEdFellows,
                        entry.fields.accessLocationHbsExecEdFellows,
                      ),
                      buildAccordionItem(
                        "HBS Alumni",
                        entry.fields.accessMethodHbsAlumni,
                        entry.fields.accessDescriptionHbsAlumni,
                        entry.fields.accessLocationHbsAlumni,
                      ),
                      buildAccordionItem(
                        "Harvard Faculty, Students, and Staff",
                        entry.fields.accessMethodHuFacultyStudentsStaff,
                        entry.fields.accessDescriptionHuFacultyStudentsStaff,
                        entry.fields.accessLocationHuFacultyStudentsStaff,
                      ),
                      buildAccordionItem(
                        "Visitors",
                        entry.fields.accessMethodVisitors,
                        entry.fields.accessDescriptionVisitors,
                        entry.fields.accessLocationVisitors,
                      ),
                    ]}
                  />

                  {/* Additional Information */}
                  {(entry.fields.shortAdditionalInformation ||
                    entry.fields.additionalInformation) && (
                    <>
                      <h2 className="hbs-text-h4">Additional Information</h2>
                      <RichText
                        editAttribute={createEditAttributes({
                          entry,
                          fieldId: "shortAdditionalInformation",
                        })}
                      >
                        <RenderContentfulRichText
                          document={entry.fields?.shortAdditionalInformation}
                        />
                      </RichText>
                      {entry.fields.additionalInformation && (
                        <Truncator
                          align="center"
                          labelHidden="Read More"
                          labelVisible="Hide"
                        >
                          <RichText
                            editAttribute={createEditAttributes({
                              entry,
                              fieldId: "additionalInformation",
                            })}
                          >
                            <RenderContentfulRichText
                              document={entry.fields.additionalInformation}
                            />
                          </RichText>
                        </Truncator>
                      )}
                    </>
                  )}
                </>
              ),
              preFooterContent: (
                <>
                  {entry.fields.relatedObjects && (
                    <PageSection
                      spacing={entry.fields.preFooterContent && "top"}
                    >
                      <TeaseRow
                        headerContent={{
                          title: "Learn More",
                          isSmall: false,
                        }}
                        teases={getRelatedObjects()}
                        teaseStyle="content"
                        align="full"
                      />
                    </PageSection>
                  )}
                  <ContentfulPageSections
                    entry={entry}
                    options={{ key: "preFooterContent" }}
                  />
                </>
              ),
            }}
          />
        </>
      );
    },
  );
